/**
 * SHOP-35: PDP buy box — Mario B tiles, Abo/Einmalig, selling-plan sync.
 * Einmalig 1 Monat displays the current E1 variant price. Starterset is
 * optional, default off. Checkbox off adds that variant plus the free gift
 * Messlöffel (queued for starter-set.js, not the paid kit Löffel). The first
 * add-to-cart on that path opens the Starterset prompt: Yes checks the kit
 * and adds the paid lines, No continues with the free spoon. Checkbox
 * on adds the four kit lines in data-kit-variant-ids and does not
 * queue the free spoon or show the prompt. The surcharge and the Zusammen total are the live
 * variant prices. The optional sub-line uses the same contents wording as the included set and does not list item prices.
 * Einmalig 3/6 send the base variant at quantity 3 or 6.
 * Abo is unchanged. The included checkbox on 3/6 and Abo does not queue the
 * paid kit lines or the free spoon.
 */
const WEEKDAYS_DE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const MONTHS_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const addBusinessDays = (from, count) => {
  const date = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let added = 0;

  while (added < count) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();

    if (day !== 0 && day !== 6) {
      added += 1;
    }
  }

  return date;
};

const formatShipDateDe = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');

  return `${WEEKDAYS_DE[date.getDay()]} ${day}.${month}.${date.getFullYear()}`;
};

const syncCartForms = (sellingPlanId, quantity, variantId) => {
  // SHOP-44: only write selling_plan / qty / id into forms that opt in.
  const forms = document.querySelectorAll('form[action*="/cart/add"][data-e1-pdp-sync]');

  for (const form of forms) {
    let planInput = form.querySelector('input[name="selling_plan"]');

    if (!planInput) {
      planInput = document.createElement('input');
      planInput.type = 'hidden';
      planInput.name = 'selling_plan';
      form.appendChild(planInput);
    }

    planInput.value = sellingPlanId || '';

    let qtyInput = form.querySelector('input[name="quantity"]');

    if (!qtyInput) {
      qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'quantity';
      form.appendChild(qtyInput);
    }

    qtyInput.value = String(quantity || 1);

    if (variantId) {
      const variantInput = form.querySelector('input[name="id"]');

      if (variantInput) {
        variantInput.value = String(variantId);
      }
    }
  }
};

const setRowSelected = (row, selected) => {
  row.dataset.selected = selected ? 'true' : 'false';
  row.classList.toggle('e1-pdp-opt--on', selected);
};

const bindPressFeedback = (el) => {
  if (!el || el.dataset.e1PressBound === 'true') {
    return;
  }

  el.dataset.e1PressBound = 'true';

  const press = () => {
    el.dataset.pressed = 'true';
  };

  const release = () => {
    el.removeAttribute('data-pressed');
  };

  el.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    press();
  });

  el.addEventListener('pointerup', release);
  el.addEventListener('pointercancel', release);
  el.addEventListener('pointerleave', release);
  el.addEventListener('blur', release);
  el.addEventListener('keydown', (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      press();
    }
  });
  el.addEventListener('keyup', (event) => {
    if (event.key === ' ' || event.key === 'Enter') {
      release();
    }
  });
};

const moneyFromRoot = (cents) => {
  const value = (Number(cents) / 100).toFixed(2).replace('.', ',');

  return `€${value}`;
};

const BUNDLE_VARIANT_ID = '51542781329741';
// BXGY gift Löffel from productToGiftMap in assets/starter-set.js.
const FREE_SPOON_GIFT_VARIANT_ID = '52417397326157';

const parseVariantIdList = (value) => {
  const seen = new Set();
  const ids = [];

  for (const part of String(value || '').split(',')) {
    const id = part.trim();

    if (!/^\d+$/.test(id) || id === BUNDLE_VARIANT_ID || seen.has(id)) {
      continue;
    }

    seen.add(id);
    ids.push(id);
  }

  return ids;
};

const initBuybox = (root) => {
  const segmentButtons = root.querySelectorAll('[data-e1-pdp-segment]');
  const panels = {
    abo: root.querySelector('[data-e1-pdp-panel="abo"]'),
    once: root.querySelector('[data-e1-pdp-panel="once"]'),
  };
  const underline = root.querySelector('[data-e1-pdp-underline]');
  const kitBox = root.querySelector('[data-e1-pdp-kitbox]');
  const kitChk = root.querySelector('[data-e1-pdp-kit-chk]');
  const kitNote = root.querySelector('[data-e1-pdp-kit-note]');
  const kitPrice = root.querySelector('[data-e1-pdp-kit-price]');
  const kitSub = root.querySelector('[data-e1-pdp-kit-sub]');
  const spoonNote = root.querySelector('[data-e1-pdp-spoon-note]');
  const kitAsk = root.querySelector('[data-e1-pdp-kit-ask]');
  const kitAskYes = root.querySelector('[data-e1-pdp-kit-ask-yes]');
  const kitAskNo = root.querySelector('[data-e1-pdp-kit-ask-no]');
  const kitAskValue = root.querySelector('[data-e1-pdp-kit-ask-value]');
  const kitAskYesText = root.querySelector('[data-e1-pdp-kit-ask-yes-text]');
  let kitAsked = false;
  const includeProp = root.querySelector('[data-e1-pdp-include-prop]');
  const kaufartProp = root.querySelector('[data-e1-pdp-kaufart-prop]');
  const refill = root.querySelector('[data-e1-pdp-refill]');
  const saveLine = root.querySelector('[data-e1-pdp-save]');
  const optMeta = root.querySelector('[data-e1-pdp-optmeta]');
  const shipText = root.querySelector('[data-e1-pdp-ship-text]');
  const atc = root.querySelector('[data-e1-pdp-atc]');
  const atcSubmit = root.querySelector('[data-e1-pdp-atc-submit]');
  const atcText = atc?.querySelector('[data-e1-pdp-atc-text]');
  const radios = root.querySelectorAll('input[type="radio"][data-e1-pdp-option]');
  const kitValue = Number(root.dataset.kitValue || 0);
  let kitAdd = Number(root.dataset.kitAdd || 0);
  const kitVariantIds = parseVariantIdList(root.dataset.kitVariantIds);
  const kitOfferReady = kitVariantIds.length === 4;
  let onceMainCents = Number(root.dataset.onceMainCents || 0);
  const kitTogether = root.querySelector('[data-e1-pdp-kit-together]');
  const kitSubIncluded = root.dataset.kitSubIncluded || 'Geschenkbox mit Glasflasche, Messlöffel und Aufbewahrungsdose';
  const kitSubOnceOne = root.dataset.kitSubOnceOne || kitSubIncluded;
  const kitNoteOptional = root.dataset.kitNoteOptional || 'zum Start empfohlen';
  const kitNoteUnavailable = root.dataset.kitNoteUnavailable || 'demnächst verfügbar';
  const underlinePlain = 'Einmalige Lieferung, kein Abo · 30 Tage Geld zurück';
  const underlineKit = 'Einmalige Lieferung plus Starterset · 30 Tage Geld zurück';
  let lastKitMode = '';
  let currentMode = root.querySelector('[data-e1-pdp-segment][aria-pressed="true"]')?.dataset.e1PdpSegment || 'once';
  const buyboxForm = root.querySelector('form[data-type="add-to-cart-form"]');
  const sellingPlanInput = buyboxForm?.querySelector('input[name="selling_plan"]');
  const quantityInput = buyboxForm?.querySelector('input[name="quantity"]');
  const variantInput = buyboxForm?.querySelector('input[name="id"]');

  const paintAtc = (cartSafe) => {
    if (!atc) {
      return;
    }

    if (atcText && atcText.textContent !== 'IN DEN WARENKORB') {
      atcText.textContent = 'IN DEN WARENKORB';
    }

    if (atc.disabled) {
      atc.disabled = false;
    }

    atc.setAttribute('aria-disabled', cartSafe ? 'false' : 'true');
  };

  if (shipText) {
    try {
      const arrival = addBusinessDays(new Date(), 3);

      shipText.innerHTML = `Versandkostenfrei, bei dir bis <b>${formatShipDateDe(arrival)}</b>`;
    } catch (error) {
      shipText.textContent = 'Versandkostenfrei, Lieferung in 1–3 Werktagen';
    }
  }

  const isOnce1OptionalRow = (row) =>
    Number(row?.dataset.e1PdpRowMonths || 0) === 1 && row?.dataset.e1PdpRowKitMode === 'optional';

  const isOptionalKit = (row) => currentMode === 'once' && isOnce1OptionalRow(row);

  const isComingSoonKitCopy = (text) => /starterset\s+demnächst\s+verfügbar|demnächst\s+verfügbar/i.test(String(text || ''));

  const stripComingSoonKitCopy = (text) =>
    String(text || '')
      .replace(/\s*·\s*Starterset\s+demnächst\s+verfügbar/gi, '')
      .replace(/Starterset\s+demnächst\s+verfügbar/gi, '')
      .replace(/\s*·\s*demnächst\s+verfügbar/gi, '')
      .replace(/demnächst\s+verfügbar/gi, '')
      .replace(/\s*·\s*$/g, '')
      .replace(/^\s*·\s*/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

  const hideOnce1ComingSoonCopy = (row) => {
    if (!row || !isOnce1OptionalRow(row) || kitOfferReady) {
      return;
    }

    const subEl = row.querySelector('.e1-pdp-opt__sub');

    if (subEl) {
      const cleaned = stripComingSoonKitCopy(subEl.textContent);
      subEl.textContent = cleaned;
      subEl.hidden = !cleaned;
    }

    if (row.dataset.e1PdpRowSub) {
      row.dataset.e1PdpRowSub = stripComingSoonKitCopy(row.dataset.e1PdpRowSub);
    }

    if (isComingSoonKitCopy(row.dataset.e1PdpRowKit)) {
      row.dataset.e1PdpRowKit = '';
    }

    if (row.dataset.e1PdpRowUnderline) {
      const cleanedUnder = stripComingSoonKitCopy(row.dataset.e1PdpRowUnderline);

      if (cleanedUnder) {
        row.dataset.e1PdpRowUnderline = cleanedUnder;
      } else if (isComingSoonKitCopy(row.dataset.e1PdpRowUnderline)) {
        row.dataset.e1PdpRowUnderline = '';
      }
    }
  };

  const resolveOnce1Cart = (row) => {
    const kitOn = Boolean(kitChk?.checked);

    if (!isOptionalKit(row)) {
      return null;
    }

    const variantId = row.querySelector('input[type="radio"][data-e1-pdp-option]')?.dataset.variantId || '';
    const kitsReady = kitOfferReady;

    if (kitOn && kitsReady && variantId) {
      return {
        variantId,
        pendingKits: kitVariantIds.slice(),
        pendingFreeSpoon: false,
        cartSafe: true,
        prop: 'none',
        cents: onceMainCents + kitAdd,
      };
    }

    if (kitOn) {
      return {
        variantId: '',
        pendingKits: [],
        pendingFreeSpoon: false,
        cartSafe: false,
        prop: 'none',
        cents: onceMainCents,
      };
    }

    if (variantId) {
      return {
        variantId,
        pendingKits: [],
        pendingFreeSpoon: true,
        cartSafe: true,
        prop: 'none',
        cents: onceMainCents,
      };
    }

    return {
      variantId: '',
      pendingKits: [],
      pendingFreeSpoon: false,
      cartSafe: false,
      prop: 'none',
      cents: onceMainCents,
    };
  };

  const syncIncludeProp = () => {
    if (includeProp) {
      includeProp.removeAttribute('name');
      includeProp.value = '';
    }

    syncKaufart(false);
  };

  const syncKaufart = (on) => {
    if (!kaufartProp) {
      return;
    }

    if (on) {
      kaufartProp.name = 'properties[Kaufart]';
      kaufartProp.value = 'Einmalkauf';
      return;
    }

    kaufartProp.removeAttribute('name');
    kaufartProp.value = '';
  };

  const syncPendingPaidKit = (pendingKits) => {
    const ids = Array.isArray(pendingKits) ? pendingKits.filter(Boolean) : [];

    window.evertyPendingPaidKitVariantIds = ids;
    window.evertyPendingPaidKitVariantId = '';
  };

  const syncPendingFreeSpoon = (queued) => {
    window.evertyPendingFreeSpoonGiftVariantId = queued ? FREE_SPOON_GIFT_VARIANT_ID : '';
  };

  const paintKitAsk = () => {
    if (kitAskValue && kitValue > 0) {
      kitAskValue.textContent = `Geschenkbox mit Glasflasche, Messlöffel und Aufbewahrungsdose. Einmalig, im Wert von ${moneyFromRoot(kitValue)}.`;
    }

    if (kitAskYesText) {
      kitAskYesText.textContent = kitAdd > 0 ? `Ja, dazu für ${moneyFromRoot(kitAdd)}` : 'Ja, Starterset dazu';
    }
  };

  const kitAskOpen = () => Boolean(kitAsk && !kitAsk.hidden);

  const setKitAskOpen = (open) => {
    if (!kitAsk) {
      return;
    }

    kitAsk.hidden = !open;

    if (!open) {
      return;
    }

    paintKitAsk();

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    kitAsk.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center',
    });
    kitAskYes?.focus();
  };

  const needsKitAsk = (row) => {
    if (!kitAsk || kitAsked || kitAskOpen() || !kitOfferReady) {
      return false;
    }

    if (!isOptionalKit(row) || kitChk?.checked) {
      return false;
    }

    return true;
  };

  const submitBuybox = () => {
    if (root.dataset.e1PdpCartSafe === 'false') {
      return;
    }

    if (typeof buyboxForm?.requestSubmit === 'function') {
      buyboxForm.requestSubmit(atcSubmit || undefined);
    } else {
      buyboxForm?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  };

  const renderKitBox = (row) => {
    if (!kitBox || !kitChk || !kitNote || !kitPrice) {
      return;
    }

    const kitMode = row?.dataset.e1PdpRowKitMode || 'included';
    const included = kitMode === 'included';
    const optional = isOptionalKit(row);

    kitBox.hidden = false;

    if (optional && lastKitMode !== 'optional') {
      kitChk.checked = false;
    }

    const once1Cart = resolveOnce1Cart(row);

    lastKitMode = optional ? 'optional' : included ? 'included' : kitMode;
    kitBox.classList.toggle('e1-pdp-kitbox--locked', included);
    kitBox.classList.toggle('e1-pdp-kitbox--offer', optional);
    kitChk.disabled = included;

    if (optional) {
      kitNote.textContent = kitNoteOptional;
      kitPrice.textContent = kitAdd > 0 ? `+${moneyFromRoot(kitAdd)}` : '';
      if (kitSub) {
        kitSub.textContent = kitSubOnceOne;
      }
      if (kitTogether) {
        if (kitChk.checked && onceMainCents > 0 && kitAdd > 0) {
          kitTogether.hidden = false;
          kitTogether.textContent = `Zusammen ${moneyFromRoot(onceMainCents + kitAdd)}`;
        } else {
          kitTogether.hidden = true;
          kitTogether.textContent = '';
        }
      }
    } else if (included) {
      kitChk.checked = true;
      kitNote.textContent = currentMode === 'abo' ? 'gratis zur ersten Lieferung' : 'ohne Aufpreis enthalten';
      kitPrice.innerHTML = `<s>${moneyFromRoot(kitValue)}</s>inklusive`;
      if (kitSub) {
        kitSub.textContent = kitSubIncluded;
      }
      if (kitTogether) {
        kitTogether.hidden = true;
        kitTogether.textContent = '';
      }
    } else {
      kitChk.checked = false;
      kitNote.textContent = kitNoteUnavailable;
      kitPrice.innerHTML = `<s>${moneyFromRoot(kitValue)}</s>${moneyFromRoot(kitAdd)}`;
      if (kitSub) {
        kitSub.textContent = kitSubIncluded;
      }
      if (kitTogether) {
        kitTogether.hidden = true;
        kitTogether.textContent = '';
      }
    }

    if (spoonNote) {
      const note = spoonNote.textContent.replace(/\s+/g, ' ').trim();
      const showSpoonNote = Boolean(optional && !kitChk.checked && note);

      spoonNote.hidden = !showSpoonNote;
    }

    paintKitAsk();

    if (!optional || kitChk.checked) {
      setKitAskOpen(false);
    }

    if (!optional) {
      syncIncludeProp();
      syncPendingPaidKit('');
      syncPendingFreeSpoon(false);
    } else if (once1Cart) {
      syncIncludeProp();
      syncPendingPaidKit(once1Cart.pendingKits);
      syncPendingFreeSpoon(once1Cart.pendingFreeSpoon);
    }
  };

  const renderSave = (row) => {
    if (!saveLine) {
      return;
    }

    const label = row?.dataset.e1PdpRowSave || '';

    if (!label) {
      saveLine.hidden = true;
      saveLine.textContent = '';
      return;
    }

    saveLine.hidden = false;
    saveLine.textContent = label;
  };

  const renderOptMeta = (row) => {
    if (!optMeta || !row) {
      return;
    }

    const sub = row.dataset.e1PdpRowSub || '';
    const details = row.dataset.e1PdpRowDetails || 'Details';
    const key = row.querySelector('[data-popup-content-key]')?.dataset.popupContentKey || '';

    optMeta.innerHTML = `${sub ? `<span>${sub}</span>` : ''}<button type="button" class="e1-pdp-opt__det" data-popup-trigger="e1-pdp-details" data-popup-content-key="${key}">${details}</button>`;
  };

  const applyChecked = () => {
    const checked = root.querySelector('input[type="radio"][data-e1-pdp-option]:checked');

    if (!checked) {
      return;
    }

    const row = checked.closest('[data-e1-pdp-row]');

    for (const radio of radios) {
      const radioRow = radio.closest('[data-e1-pdp-row]');

      if (radioRow) {
        setRowSelected(radioRow, radio === checked);
      }
    }

    for (const optionRow of root.querySelectorAll('[data-e1-pdp-row]')) {
      hideOnce1ComingSoonCopy(optionRow);
    }

    renderKitBox(row);

    const once1Cart = resolveOnce1Cart(row);
    let cartSafe = checked.dataset.cartSafe !== 'false';

    if (once1Cart) {
      cartSafe = once1Cart.cartSafe;
    }

    if (underline) {
      let nextUnder = row?.dataset.e1PdpRowUnderline || '';

      if (once1Cart) {
        nextUnder = kitChk?.checked ? underlineKit : underlinePlain;
      }

      if (nextUnder) {
        underline.hidden = false;
        underline.textContent = nextUnder;
      }
    }

    renderSave(row);
    renderOptMeta(row);

    if (refill) {
      refill.hidden = true;
    }

    paintAtc(cartSafe);

    // Abo always qty 1 + selling plan. Einmalig 3/6 use the base variant
    // at data-quantity 3 or 6 when cart_safe (Automatic Discount in cart).
    // Einmalig ×1 uses the catalog E1 variant. Starterset on queues the four
    // paid kit lines. Starterset off queues the free gift Messlöffel.
    let quantity = currentMode === 'abo' ? '1' : cartSafe ? checked.dataset.quantity || '1' : '1';
    let sellingPlanId = currentMode === 'abo' ? checked.dataset.sellingPlanId || '' : '';
    let variantId = checked.dataset.variantId || '';

    if (once1Cart) {
      quantity = '1';
      sellingPlanId = '';
      variantId = once1Cart.variantId || '';
    }

    if (sellingPlanInput) {
      sellingPlanInput.value = cartSafe ? sellingPlanId : '';
    }

    if (quantityInput) {
      quantityInput.value = String(quantity);
    }

    if (variantInput) {
      if (cartSafe && variantId) {
        variantInput.value = String(variantId);
      } else if (once1Cart) {
        variantInput.value = '';
      }
    }

    if (cartSafe && variantId) {
      syncCartForms(sellingPlanId, quantity, variantId);
    } else if (!once1Cart) {
      syncCartForms('', '1', variantId);
    }

    root.dataset.e1PdpCartSafe = cartSafe ? 'true' : 'false';

    const stickyLabel = document.querySelector('#sticky-add-to-cart .sticky-cart-label');
    const stickyPrice = document.querySelector('#sticky-add-to-cart .price-final-abo');
    const stickyDaily = document.querySelector('#sticky-add-to-cart .sticky-cart-meta .unit-price');

    if (row) {
      if (stickyLabel && row.dataset.e1PdpRowTitle) {
        stickyLabel.textContent = row.dataset.e1PdpRowTitle;
      }

      if (stickyPrice && once1Cart && kitChk?.checked) {
        stickyPrice.textContent = moneyFromRoot(once1Cart.cents);
      } else if (stickyPrice && row.dataset.e1PdpRowPrice) {
        stickyPrice.textContent = row.dataset.e1PdpRowPrice;
      }

      if (stickyDaily && row.dataset.e1PdpRowDaily) {
        stickyDaily.textContent = row.dataset.e1PdpRowDaily;
      }
    }
  };

  const setSegment = (mode) => {
    const nextMode = mode === 'once' ? 'once' : 'abo';
    const keepSelection = nextMode === currentMode;
    currentMode = nextMode;

    for (const button of segmentButtons) {
      button.setAttribute('aria-pressed', button.dataset.e1PdpSegment === currentMode ? 'true' : 'false');
    }

    if (panels.abo) {
      panels.abo.hidden = currentMode !== 'abo';
    }

    if (panels.once) {
      panels.once.hidden = currentMode !== 'once';
    }

    const panel = currentMode === 'once' ? panels.once : panels.abo;
    const preferred = panel
      ? panel.querySelector('input[type="radio"][data-e1-pdp-option][data-preferred="true"]')
        || panel.querySelector('input[type="radio"][data-e1-pdp-option]')
      : null;

    // Re-clicking the active segment must not jump back to the preferred
    // 3-month tile. That tile submits quantity 3 and the pack discount.
    if (preferred && !keepSelection) {
      preferred.checked = true;
    }

    applyChecked();
  };

  for (const button of segmentButtons) {
    button.addEventListener('click', () => {
      setSegment(button.dataset.e1PdpSegment);
    });
  }

  for (const radio of radios) {
    radio.addEventListener('change', applyChecked);
  }

  for (const row of root.querySelectorAll('[data-e1-pdp-row]')) {
    row.addEventListener('click', (event) => {
      if (event.target.closest('[data-popup-trigger]')) {
        return;
      }

      const radio = row.querySelector('input[type="radio"][data-e1-pdp-option]');

      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  if (kitChk) {
    kitChk.addEventListener('click', (event) => {
      if (kitChk.disabled) {
        event.preventDefault();
      }
    });

    kitChk.addEventListener('change', () => {
      const checked = root.querySelector('input[type="radio"][data-e1-pdp-option]:checked');
      const row = checked?.closest('[data-e1-pdp-row]');

      if (!isOptionalKit(row)) {
        kitChk.checked = row?.dataset.e1PdpRowKitMode === 'included';
        return;
      }

      applyChecked();
    });
  }

  const form = buyboxForm;

  if (form) {
    // Capture runs before product-form reads FormData. 1 Monat is quantity 1,
    // no selling plan, catalog E1 variant. Starterset kits are queued beside it.
    // once_1 with the kit off stops here the first time and shows the prompt.
    form.addEventListener('submit', (event) => {
      applyChecked();

      const checked = root.querySelector('input[type="radio"][data-e1-pdp-option]:checked');
      const row = checked?.closest('[data-e1-pdp-row]');

      if (needsKitAsk(row) || kitAskOpen()) {
        event.preventDefault();
        event.stopPropagation();

        if (needsKitAsk(row)) {
          kitAsked = true;
          setKitAskOpen(true);
        }

        return;
      }

      if (root.dataset.e1PdpCartSafe === 'false') {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);
  }

  const continueAfterKitAsk = (withKit) => {
    if (kitChk && !kitChk.disabled) {
      kitChk.checked = withKit;
    }

    kitAsked = true;
    setKitAskOpen(false);
    applyChecked();
    submitBuybox();
  };

  kitAskYes?.addEventListener('click', () => {
    continueAfterKitAsk(true);
  });

  kitAskNo?.addEventListener('click', () => {
    continueAfterKitAsk(false);
  });

  if (atc) {
    bindPressFeedback(atc);

    atc.addEventListener('click', (event) => {
      event.preventDefault();
      applyChecked();

      if (root.dataset.e1PdpCartSafe === 'false') {
        return;
      }

      const checked = root.querySelector('input[type="radio"][data-e1-pdp-option]:checked');
      const row = checked?.closest('[data-e1-pdp-row]');

      if (needsKitAsk(row)) {
        kitAsked = true;
        setKitAskOpen(true);
        return;
      }

      if (kitAskOpen()) {
        return;
      }

      submitBuybox();
    });
  }

  bindPressFeedback(document.getElementById('sticky-add-to-cart-btn'));

  if (!kitOfferReady) {
    for (const optionRow of root.querySelectorAll('[data-e1-pdp-row]')) {
      hideOnce1ComingSoonCopy(optionRow);
    }

    const detailsStore = window['e1PopupContent_e1-pdp-details'];

    if (detailsStore && typeof detailsStore.once1 === 'string') {
      detailsStore.once1 = detailsStore.once1.replace(
        /<dt>\s*Starterset\s*<\/dt>\s*<dd>[^<]*demnächst[^<]*<\/dd>/i,
        ''
      );
    }
  }

  const refreshAdminPrices = async () => {
    const handles = String(root.dataset.kitHandles || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);

    if (!handles.length) {
      return;
    }

    const rootPath = window.Shopify?.routes?.root || '/';
    const units = [];

    for (const handle of handles) {
      const response = await fetch(`${rootPath}products/${handle}.js`, { credentials: 'same-origin' });

      if (!response.ok) {
        return;
      }

      const product = await response.json();
      const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0];

      if (!variant) {
        return;
      }

      units.push(Number(variant.price));
    }

    if (units.length !== handles.length) {
      return;
    }

    kitAdd = units.reduce((sum, cents) => sum + cents, 0);
    root.dataset.kitAdd = String(kitAdd);
    root.dataset.kitUnitCents = units.join(',');

    const onceRow = root.querySelector('[data-e1-pdp-panel="once"] [data-e1-pdp-row-months="1"]');
    const onceSub = onceRow?.querySelector('.e1-pdp-opt__sub');

    if (onceRow && kitAdd > 0) {
      const subtitle = `Starterset optional +${moneyFromRoot(kitAdd)}`;
      onceRow.dataset.e1PdpRowSub = subtitle;

      if (onceSub) {
        onceSub.textContent = subtitle;
      }
    }

    applyChecked();
  };

  setSegment(currentMode);
  refreshAdminPrices().catch((error) => {
    console.error('Starterset prices failed', error);
  });

  if (atc) {
    const observer = new MutationObserver(() => {
      const checked = root.querySelector('input[type="radio"][data-e1-pdp-option]:checked');
      const cartSafe = checked ? checked.dataset.cartSafe !== 'false' : true;
      paintAtc(cartSafe);
    });

    observer.observe(atc, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }
};

const boot = () => {
  const roots = document.querySelectorAll('[data-e1-pdp-buybox]');

  for (const root of roots) {
    if (root.dataset.e1PdpReady === 'true') {
      continue;
    }

    root.dataset.e1PdpReady = 'true';
    initBuybox(root);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener('shopify:section:load', boot);
